/**
 * DeskeyPaint — MS Paint / jspaint parity (hybrid Konva engine)
 * Tools: Select(rect+lasso), Pen, Brush(tips), Airbrush, Eraser, Eyedropper,
 *        Fill, Line, Curve(3-click), Arrow, DoubleArrow, Polygon, Shapes, Text, Magnifier
 * Menu: Image > Flip H/V, Rotate 90, Invert Colors, Clear
 * Clipboard: Cut/Copy/Paste via Ctrl+X/C/V
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Stage, Layer, Rect, Circle, Ellipse, Line, Arrow, Star, RegularPolygon, Text, Transformer, Group, Image as KImage, Path } from 'react-konva';
import useCollabRoom from '../hooks/useCollabRoom';
import CollabBadge from './CollabBadge';

const COLORS = ['#1e293b','#374151','#6b7280','#9ca3af','#ef4444','#f97316','#eab308','#22c55e','#34A853','#0d9488','#3b82f6','#6366f1','#8b5cf6','#ec4899','#ffffff'];
const FONTS = ['Arial','Helvetica','Georgia','Courier New','Verdana','Trebuchet MS','Impact','Comic Sans MS','Times New Roman','Palatino','Garamond','Bookman'];
const SIZES = [8,10,12,14,16,18,20,24,28,32,36,42,48,56,64,72,96];
const SHAPES = [
  {id:'rect',label:'Rectangle',cat:'basic'},{id:'roundRect',label:'Rounded Rect',cat:'basic'},
  {id:'ellipse',label:'Ellipse',cat:'basic'},{id:'circle',label:'Circle',cat:'basic'},
  {id:'triangle',label:'Triangle',cat:'basic'},{id:'diamond',label:'Diamond',cat:'basic'},
  {id:'star',label:'Star',cat:'basic'},{id:'hexagon',label:'Hexagon',cat:'flow'},
  {id:'pentagon',label:'Pentagon',cat:'flow'},{id:'octagon',label:'Octagon',cat:'flow'},
  {id:'trapezoid',label:'Trapezoid',cat:'flow'},{id:'cylinder',label:'Cylinder',cat:'flow'},
  {id:'cloud',label:'Cloud',cat:'flow'},
];
const LINE_WIDTHS = [1,2,3,4,5,8,12];
const ZOOMS = [0.5, 1, 2, 4, 8];
const BRUSH_TIPS = [
  { id:'circle', label:'●' },
  { id:'square', label:'■' },
  { id:'slash', label:'╱' },
];
const FILL_MODES = [
  { id:'outline', label:'Outline', desc:'Stroke only' },
  { id:'both', label:'Both', desc:'Stroke + Fill' },
  { id:'fill', label:'Fill', desc:'Fill only' },
];

// ── Pixel flood fill (iterative, stack-based) ──
function floodFill(ctx, startX, startY, fillR, fillG, fillB, fillA, w, h) {
  const imageData = ctx.getImageData(0, 0, w, h);
  const data = imageData.data;
  const startIdx = (startY * w + startX) * 4;
  const tR = data[startIdx], tG = data[startIdx+1], tB = data[startIdx+2], tA = data[startIdx+3];
  if (tR === fillR && tG === fillG && tB === fillB && tA === fillA) return;
  const tol = 32;
  const match = (i) => Math.abs(data[i]-tR)<=tol && Math.abs(data[i+1]-tG)<=tol && Math.abs(data[i+2]-tB)<=tol && Math.abs(data[i+3]-tA)<=tol;
  const stack = [[startX, startY]];
  const visited = new Uint8Array(w * h);
  while (stack.length > 0) {
    const [x, y] = stack.pop();
    if (x < 0 || x >= w || y < 0 || y >= h) continue;
    const vi = y * w + x;
    if (visited[vi]) continue;
    const i = vi * 4;
    if (!match(i)) continue;
    visited[vi] = 1;
    data[i] = fillR; data[i+1] = fillG; data[i+2] = fillB; data[i+3] = fillA;
    stack.push([x+1,y],[x-1,y],[x,y+1],[x,y-1]);
  }
  ctx.putImageData(imageData, 0, 0);
}

const hexToRgba = (hex) => {
  const h = hex.startsWith('#') ? hex.slice(1) : hex;
  return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16), 255];
};
const rgbToHex = (r,g,b) => '#' + [r,g,b].map(v => v.toString(16).padStart(2,'0')).join('');

// ── Raster transforms (operate on stage canvas) ──
function transformRaster(srcCanvas, w, h, op) {
  const out = document.createElement('canvas');
  out.width = w; out.height = h;
  const ctx = out.getContext('2d');
  if (op === 'flipH') { ctx.translate(w, 0); ctx.scale(-1, 1); ctx.drawImage(srcCanvas, 0, 0); }
  else if (op === 'flipV') { ctx.translate(0, h); ctx.scale(1, -1); ctx.drawImage(srcCanvas, 0, 0); }
  else if (op === 'rotate90') {
    out.width = h; out.height = w;
    const c2 = out.getContext('2d');
    c2.translate(h, 0); c2.rotate(Math.PI/2); c2.drawImage(srcCanvas, 0, 0);
  } else if (op === 'invert') {
    ctx.drawImage(srcCanvas, 0, 0);
    const id = ctx.getImageData(0, 0, w, h);
    for (let i = 0; i < id.data.length; i += 4) {
      id.data[i] = 255 - id.data[i];
      id.data[i+1] = 255 - id.data[i+1];
      id.data[i+2] = 255 - id.data[i+2];
    }
    ctx.putImageData(id, 0, 0);
  } else if (op === 'clear') {
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h);
  } else ctx.drawImage(srcCanvas, 0, 0);
  return out;
}

export default function DeskeyPaint({ onSave, onClose }) {
  const stageRef = useRef(null);
  const trRef = useRef(null);
  const fileRef = useRef(null);
  const textRef = useRef(null);
  const rasterRef = useRef(null);
  const airbrushTimerRef = useRef(null);
  const marchRef = useRef(null);
  const [tool, setTool] = useState('select');
  const [objects, setObjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [strokeColor, setStrokeColor] = useState('#1e293b');
  const [fillColor, setFillColor] = useState('');
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [fillMode, setFillMode] = useState('outline'); // outline|both|fill
  const [brushTip, setBrushTip] = useState('circle');
  const [fontSize, setFontSize] = useState(20);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontBold, setFontBold] = useState(false);
  const [fontItalic, setFontItalic] = useState(false);
  const [fontUnderline, setFontUnderline] = useState(false);
  const [textAlign, setTextAlign] = useState('left');
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [shapeType, setShapeType] = useState('rect');
  const [showGrid, setShowGrid] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [histStack, setHistStack] = useState([{ objects: [], rasterData: null }]);
  const [histIdx, setHistIdx] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);
  const [images, setImages] = useState({});
  const [rasterImg, setRasterImg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });
  // ── Realtime collab — opt-in via ?collab_room=<id> in the URL, or by
  //    clicking the Share button which generates a stable room id from
  //    the current canvas. Each peer's stroke commits are broadcast as
  //    'sync' events containing the full objects array (last-write-wins).
  //    For frequent sync we throttle to ~150ms.
  const [collabRoom, setCollabRoom] = useState(() => {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get('collab_room') || null;
    } catch (_e) { return null; }
  });
  const lastSyncRef = useRef(0);
  const handleCollabMessage = useCallback((m) => {
    if (m.kind !== 'sync') return;
    if (Array.isArray(m.objects)) setObjects(m.objects);
  }, []);
  const { send: collabSend, peerCount, status: collabStatus } =
    useCollabRoom(collabRoom, { onMessage: handleCollabMessage });
  // Broadcast on every objects change (throttled).
  useEffect(() => {
    if (!collabRoom) return;
    const now = Date.now();
    if (now - lastSyncRef.current < 150) return;
    lastSyncRef.current = now;
    collabSend({ kind: 'sync', objects });
  }, [objects, collabRoom, collabSend]);
  const startCollab = () => {
    const id = collabRoom || ('paint-' + Math.random().toString(36).slice(2, 10));
    setCollabRoom(id);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('collab_room', id);
      window.history.replaceState({}, '', url.toString());
    } catch (_e) { /* ignore */ }
  };

  // jspaint-style selection
  const [selection, setSelection] = useState(null); // {x,y,w,h, dashOffset}
  const [floatSel, setFloatSel] = useState(null);   // {x,y,w,h,image(HTMLImage), origX, origY}
  const clipboardRef = useRef(null); // {dataURL, w, h}
  // curve tool state
  const [curveStage, setCurveStage] = useState([]); // [p1, p2] so far, on 3rd click we commit
  // polygon tool state
  const [polyPts, setPolyPts] = useState([]);
  const skipBlurRef = useRef(false);
  const CW = 1200, CH = 680;

  // Init raster canvas
  useEffect(() => {
    const c = document.createElement('canvas');
    c.width = CW; c.height = CH;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CW, CH);
    rasterRef.current = c;
  }, []);

  // History (vector + raster snapshot)
  const saveHistory = useCallback((objs, rasterData) => {
    const snap = { objects: JSON.parse(JSON.stringify(objs)), rasterData: rasterData || null };
    setHistStack(prev => [...prev.slice(0, histIdx+1), snap].slice(-40));
    setHistIdx(prev => Math.min(prev + 1, 39));
  }, [histIdx]);

  const loadHistory = (snap) => {
    setObjects(JSON.parse(JSON.stringify(snap.objects)));
    setSelectedId(null); setEditingTextId(null); setFloatSel(null); setSelection(null);
    if (snap.rasterData) {
      const img = new window.Image();
      img.onload = () => setRasterImg(img);
      img.src = snap.rasterData;
    } else {
      setRasterImg(null);
    }
  };
  const undo = useCallback(() => { if (histIdx > 0) { setHistIdx(histIdx-1); loadHistory(histStack[histIdx-1]); } }, [histIdx, histStack]);
  const redo = useCallback(() => { if (histIdx < histStack.length-1) { setHistIdx(histIdx+1); loadHistory(histStack[histIdx+1]); } }, [histIdx, histStack]);
  const uid = () => `o${Date.now()}_${Math.random().toString(36).slice(2,6)}`;

  // Refs for stable keyboard handler
  const r = useRef({});
  r.current = { objects, selectedId, editingTextId, selection, floatSel, undo, redo, saveHistory, rasterImg };

  useEffect(() => {
    const fn = (e) => {
      if (r.current.editingTextId) return;
      // Delete
      if ((e.key==='Delete'||e.key==='Backspace')&&r.current.selectedId) {
        e.preventDefault();
        const n=r.current.objects.filter(o=>o.id!==r.current.selectedId);
        setObjects(n); r.current.saveHistory(n, r.current.rasterImg?.src); setSelectedId(null);
      }
      // Undo/Redo
      if ((e.ctrlKey||e.metaKey)&&e.key==='z'&&!e.shiftKey) { e.preventDefault(); r.current.undo(); }
      if ((e.ctrlKey||e.metaKey)&&(e.key==='y'||(e.shiftKey&&e.key==='z'))) { e.preventDefault(); r.current.redo(); }
      // Clipboard
      if ((e.ctrlKey||e.metaKey)&&e.key==='c') { e.preventDefault(); copySelection(); }
      if ((e.ctrlKey||e.metaKey)&&e.key==='x') { e.preventDefault(); cutSelection(); }
      if ((e.ctrlKey||e.metaKey)&&e.key==='v') { e.preventDefault(); pasteClipboard(); }
      if ((e.ctrlKey||e.metaKey)&&e.key==='a') { e.preventDefault(); selectAll(); }
      // Escape to cancel floating selection / polygon / curve
      if (e.key==='Escape') {
        if (r.current.floatSel) commitFloatingSelection();
        setCurveStage([]); setPolyPts([]); setSelection(null);
      }
      if (e.key==='Enter' && polyPts.length >= 3) commitPolygon();
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [polyPts]);

  // Transformer sync
  useEffect(() => {
    if (!trRef.current || !stageRef.current) return;
    if (selectedId && !editingTextId) {
      const node = stageRef.current.findOne('#'+selectedId);
      if (node) { trRef.current.nodes([node]); trRef.current.getLayer()?.batchDraw(); }
      else trRef.current.nodes([]);
    } else trRef.current.nodes([]);
  }, [selectedId, editingTextId, objects]);

  // Marching ants animation for selection
  useEffect(() => {
    if (!selection && !floatSel) { if (marchRef.current) { cancelAnimationFrame(marchRef.current); marchRef.current=null; } return; }
    let off = 0;
    const tick = () => { off = (off+1) % 8; setSelection(s => s ? { ...s, dashOffset: off } : s); setFloatSel(f => f ? { ...f, dashOffset: off } : f); marchRef.current = requestAnimationFrame(tick); };
    marchRef.current = requestAnimationFrame(tick);
    return () => { if (marchRef.current) cancelAnimationFrame(marchRef.current); };
  }, [!!selection, !!floatSel]);

  const getPos = () => {
    const p = stageRef.current?.getRelativePointerPosition();
    return p ? { x: p.x, y: p.y } : { x: 0, y: 0 };
  };

  // ── Flatten stage to a raster canvas (composites raster + vectors at 1:1 scale) ──
  const flattenStage = () => {
    const stage = stageRef.current;
    const prevSX = stage.scaleX(), prevSY = stage.scaleY();
    stage.scale({ x: 1, y: 1 });
    const c = document.createElement('canvas');
    c.width = CW; c.height = CH;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, CW, CH);
    try {
      const stageCanvas = stage.toCanvas({ pixelRatio: 1, width: CW, height: CH });
      ctx.drawImage(stageCanvas, 0, 0);
    } catch (e) { console.error(e); }
    stage.scale({ x: prevSX, y: prevSY });
    return c;
  };

  // ── Eyedropper ──
  const pickColor = (x, y) => {
    const c = flattenStage();
    const ctx = c.getContext('2d');
    const data = ctx.getImageData(Math.round(x), Math.round(y), 1, 1).data;
    const hex = rgbToHex(data[0], data[1], data[2]);
    setStrokeColor(hex);
    setTool('select');
  };

  // ── Flood fill ──
  const doFloodFill = (x, y, color) => {
    const stageCanvas = flattenStage();
    const raster = rasterRef.current;
    const ctx = raster.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, CW, CH);
    ctx.drawImage(stageCanvas, 0, 0);
    const px = Math.round(x), py = Math.round(y);
    if (px >= 0 && px < CW && py >= 0 && py < CH) {
      const [fr, fg, fb, fa] = hexToRgba(color);
      floodFill(ctx, px, py, fr, fg, fb, fa, CW, CH);
    }
    const img = new window.Image();
    img.onload = () => {
      setRasterImg(img);
      setObjects([]);
      saveHistory([], raster.toDataURL());
    };
    img.src = raster.toDataURL();
  };

  // ── Airbrush ──
  const airbrushBakeFirst = () => {
    // Flatten all vectors + raster into the raster layer so spray draws on top of everything
    const flat = flattenStage();
    const ctx = rasterRef.current.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, CW, CH);
    ctx.drawImage(flat, 0, 0);
    setObjects([]);
  };
  const airbrushTick = (pos) => {
    const raster = rasterRef.current;
    const ctx = raster.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = strokeColor;
    const density = 12 + strokeWidth * 3;
    const radius = Math.max(6, strokeWidth * 4);
    for (let i = 0; i < density; i++) {
      const a = Math.random() * Math.PI * 2;
      const r2 = Math.random() * radius;
      const dx = Math.cos(a) * r2, dy = Math.sin(a) * r2;
      ctx.fillRect(Math.round(pos.x + dx), Math.round(pos.y + dy), 1, 1);
    }
    const img = new window.Image();
    img.onload = () => setRasterImg(img);
    img.src = raster.toDataURL();
  };

  // ── Rectangular selection: cut pixels from stage → floating selection ──
  const commitRectSelection = (x, y, w, h) => {
    if (w < 3 || h < 3) { setSelection(null); return; }
    const stageCanvas = flattenStage();
    const ix = Math.max(0, Math.round(x)), iy = Math.max(0, Math.round(y));
    const iw = Math.min(CW - ix, Math.round(w)), ih = Math.min(CH - iy, Math.round(h));
    if (iw < 1 || ih < 1) { setSelection(null); return; }
    // extract region
    const frag = document.createElement('canvas');
    frag.width = iw; frag.height = ih;
    frag.getContext('2d').drawImage(stageCanvas, ix, iy, iw, ih, 0, 0, iw, ih);
    // fill white where extracted (in raster)
    const ctx = rasterRef.current.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, CW, CH);
    ctx.drawImage(stageCanvas, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(ix, iy, iw, ih);
    // set background image
    const bg = new window.Image();
    bg.onload = () => setRasterImg(bg);
    bg.src = rasterRef.current.toDataURL();
    // set floating selection
    const fimg = new window.Image();
    fimg.onload = () => {
      setObjects([]);
      setFloatSel({ x: ix, y: iy, w: iw, h: ih, image: fimg, dataURL: frag.toDataURL(), dashOffset: 0 });
      setSelection(null);
    };
    fimg.src = frag.toDataURL();
  };

  const commitFloatingSelection = () => {
    if (!floatSel) return;
    // bake floating selection back to raster at current position
    const ctx = rasterRef.current.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(floatSel.image, floatSel.x, floatSel.y, floatSel.w, floatSel.h);
    const bg = new window.Image();
    bg.onload = () => {
      setRasterImg(bg);
      saveHistory([], rasterRef.current.toDataURL());
      setFloatSel(null);
    };
    bg.src = rasterRef.current.toDataURL();
  };

  const copySelection = () => {
    if (floatSel) { clipboardRef.current = { dataURL: floatSel.dataURL, w: floatSel.w, h: floatSel.h }; return; }
    if (selection) {
      const stageCanvas = flattenStage();
      const frag = document.createElement('canvas');
      frag.width = selection.w; frag.height = selection.h;
      frag.getContext('2d').drawImage(stageCanvas, selection.x, selection.y, selection.w, selection.h, 0, 0, selection.w, selection.h);
      clipboardRef.current = { dataURL: frag.toDataURL(), w: selection.w, h: selection.h };
    }
  };
  const cutSelection = () => { copySelection(); if (floatSel) { setFloatSel(null); saveHistory([], rasterRef.current.toDataURL()); } else if (selection) { commitRectSelection(selection.x, selection.y, selection.w, selection.h); setTimeout(()=>setFloatSel(null),30); } };
  const pasteClipboard = () => {
    if (!clipboardRef.current) return;
    if (floatSel) commitFloatingSelection();
    const img = new window.Image();
    img.onload = () => setFloatSel({ x: 20, y: 20, w: clipboardRef.current.w, h: clipboardRef.current.h, image: img, dataURL: clipboardRef.current.dataURL, dashOffset: 0 });
    img.src = clipboardRef.current.dataURL;
    setTool('select');
  };
  const selectAll = () => { setSelection({ x: 0, y: 0, w: CW, h: CH, dashOffset: 0 }); setTool('rectSelect'); };

  // ── Image operations ──
  const applyImageOp = (op) => {
    if (floatSel) commitFloatingSelection();
    const src = flattenStage();
    const out = transformRaster(src, CW, CH, op);
    const img = new window.Image();
    img.onload = () => {
      setRasterImg(img);
      setObjects([]);
      saveHistory([], out.toDataURL());
    };
    img.src = out.toDataURL();
    // also update hidden raster canvas
    const ctx = rasterRef.current.getContext('2d', { willReadFrequently: true });
    ctx.clearRect(0, 0, CW, CH);
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, CW, CH);
    ctx.drawImage(out, 0, 0);
    setShowImageMenu(false);
  };

  // ── Stage handlers ──
  const onStageDown = (e) => {
    const target = e.target;
    const stage = target.getStage();
    const isBg = target === stage || ['bg','raster','selbg'].includes(target.attrs?.name);
    const pos = getPos();

    // Magnifier: click to zoom in, right-click to zoom out (handled via button)
    if (tool === 'magnifier') {
      const idx = ZOOMS.indexOf(zoom);
      const next = e.evt?.button === 2 ? Math.max(0, idx-1) : Math.min(ZOOMS.length-1, idx+1);
      setZoom(ZOOMS[next]);
      return;
    }

    // If there's a floating selection and click is outside it → commit it
    if (floatSel && (pos.x < floatSel.x || pos.x > floatSel.x + floatSel.w || pos.y < floatSel.y || pos.y > floatSel.y + floatSel.h)) {
      commitFloatingSelection();
    }

    // Eyedropper
    if (tool === 'eyedropper') { pickColor(pos.x, pos.y); return; }

    // Fill
    if (tool === 'fill') { doFloodFill(pos.x, pos.y, fillColor || strokeColor); return; }

    // Rectangular select
    if (tool === 'rectSelect') {
      setSelection({ x: pos.x, y: pos.y, w: 0, h: 0, dashOffset: 0, creating: true, startX: pos.x, startY: pos.y });
      setIsDrawing(true); setDrawStart(pos);
      return;
    }

    // Curve: 3-click state machine
    if (tool === 'curve') {
      const n = [...curveStage, pos];
      if (n.length === 3) {
        // commit quadratic curve
        const [p1, p2, ctrl] = n;
        const obj = { id:uid(), type:'curve', points:[p1.x,p1.y, ctrl.x,ctrl.y, p2.x,p2.y], stroke:strokeColor, strokeWidth, draggable:true };
        const nx = [...objects, obj];
        setObjects(nx); saveHistory(nx, rasterImg?.src); setCurveStage([]);
      } else setCurveStage(n);
      return;
    }

    // Polygon: click to add, double-click/Enter to close
    if (tool === 'polygon') {
      setPolyPts(prev => [...prev, pos]);
      return;
    }

    // Airbrush: start spraying
    if (tool === 'airbrush') {
      setIsDrawing(true);
      airbrushBakeFirst();
      airbrushTick(pos);
      airbrushTimerRef.current = setInterval(() => { const p = getPos(); airbrushTick(p); }, 40);
      return;
    }

    if (!isBg && tool === 'select') {
      // Clicking on another object while editing text → commit the text first
      if (editingTextId && target.id?.() !== editingTextId && target.parent?.id?.() !== editingTextId) commitText();
      return;
    }
    if (isBg) { if (editingTextId) commitText(); setSelectedId(null); }
    if (tool === 'select') return;

    // Text
    if (tool === 'text') {
      setIsDrawing(true); setDrawStart(pos);
      setObjects(prev => [...prev, { id:uid(), type:'text', x:pos.x, y:pos.y, text:'', fontSize, fontFamily,
        fontStyle:`${fontItalic?'italic ':''}${fontBold?'bold':'normal'}`, textDecoration:fontUnderline?'underline':'',
        align:textAlign, fill:strokeColor, draggable:true, width:4, height:4, isNew:true }]);
      return;
    }

    // Freehand
    if (['pen','marker','highlighter','eraser','brush'].includes(tool)) {
      setIsDrawing(true);
      let sw = strokeWidth;
      let color = strokeColor;
      let opacity = 1;
      let cap = 'round', join = 'round';
      if (tool==='marker') sw = Math.max(strokeWidth*3, 8);
      else if (tool==='highlighter') { sw = Math.max(strokeWidth*5, 18); opacity = 0.35; }
      else if (tool==='eraser') { sw = Math.max(strokeWidth*4, 20); color = '#ffffff'; }
      else if (tool==='brush') {
        sw = Math.max(strokeWidth*2, 6);
        if (brushTip==='square') { cap = 'butt'; join = 'miter'; }
      }
      setObjects(prev => [...prev, { id:uid(), type:'freehand', points:[pos.x,pos.y], stroke:color, strokeWidth:sw, opacity, cap, join, tip:brushTip, draggable:false }]);
      return;
    }

    // Lines / arrows
    if (['line','arrow','doubleArrow'].includes(tool)) {
      setIsDrawing(true); setDrawStart(pos);
      setObjects(prev => [...prev, { id:uid(), type:tool, points:[pos.x,pos.y,pos.x,pos.y], stroke:strokeColor, strokeWidth, draggable:true }]);
      return;
    }

    // Shape
    if (tool === 'shape') {
      setIsDrawing(true); setDrawStart(pos);
      const appliedFill = fillMode === 'outline' ? '' : (fillColor || strokeColor);
      const appliedStroke = fillMode === 'fill' ? '' : strokeColor;
      setObjects(prev => [...prev, { id:uid(), type:'shape', shapeType, x:pos.x, y:pos.y, width:1, height:1, stroke:appliedStroke, strokeWidth, fill:appliedFill, fillMode, draggable:true }]);
    }
  };

  const onStageMove = () => {
    const pos = getPos();
    setCursor({ x: Math.round(pos.x), y: Math.round(pos.y) });

    // Floating selection drag
    if (floatSel?.dragging) {
      setFloatSel(f => ({ ...f, x: pos.x - f.dragOffX, y: pos.y - f.dragOffY }));
      return;
    }

    if (!isDrawing) return;

    // Rectangular selection drawing
    if (tool === 'rectSelect' && selection) {
      const x0 = selection.startX, y0 = selection.startY;
      setSelection(s => ({ ...s, x: Math.min(x0, pos.x), y: Math.min(y0, pos.y), w: Math.abs(pos.x - x0), h: Math.abs(pos.y - y0) }));
      return;
    }

    setObjects(prev => {
      const last = prev[prev.length-1]; if (!last) return prev;
      const cp = [...prev];
      if (last.type==='freehand') cp[cp.length-1] = { ...last, points:[...last.points,pos.x,pos.y] };
      else if (['line','arrow','doubleArrow'].includes(last.type)) cp[cp.length-1] = { ...last, points:[last.points[0],last.points[1],pos.x,pos.y] };
      else if (last.type==='shape'&&drawStart) cp[cp.length-1] = { ...last, width:pos.x-drawStart.x, height:pos.y-drawStart.y };
      else if (last.type==='text'&&last.isNew&&drawStart) cp[cp.length-1] = { ...last, width:Math.max(20,pos.x-drawStart.x), height:Math.max(20,pos.y-drawStart.y) };
      return cp;
    });
  };

  const onStageUp = () => {
    // Stop airbrush
    if (airbrushTimerRef.current) { clearInterval(airbrushTimerRef.current); airbrushTimerRef.current = null; saveHistory(objects, rasterRef.current.toDataURL()); }

    // Commit floating selection drag
    if (floatSel?.dragging) { setFloatSel(f => ({ ...f, dragging: false })); return; }

    if (!isDrawing) return;
    setIsDrawing(false); setDrawStart(null);

    // Commit rect selection
    if (tool === 'rectSelect' && selection) {
      const { x, y, w, h } = selection;
      if (w > 2 && h > 2) commitRectSelection(x, y, w, h);
      else setSelection(null);
      return;
    }

    const last = objects[objects.length-1];
    if (last?.type==='text'&&last.isNew) {
      const w = Math.max(80,last.width||200), h = Math.max(30,last.height||30);
      const fin = objects.map((o,i)=>i===objects.length-1?{...o,width:w,height:h,isNew:false}:o);
      setObjects(fin); saveHistory(fin, rasterImg?.src);
      skipBlurRef.current = true; setTimeout(()=>{skipBlurRef.current=false;},400);
      setSelectedId(last.id); setEditingTextId(last.id);
    } else if (last?.type==='shape') {
      // Normalize negative width/height so drag/transform math is consistent
      const nw = Math.abs(last.width || 1);
      const nh = Math.abs(last.height || 1);
      const nx = (last.width || 0) < 0 ? last.x + (last.width || 0) : last.x;
      const ny = (last.height || 0) < 0 ? last.y + (last.height || 0) : last.y;
      const fin = objects.map((o,i)=>i===objects.length-1?{...o,x:nx,y:ny,width:Math.max(4,nw),height:Math.max(4,nh)}:o);
      setObjects(fin); saveHistory(fin, rasterImg?.src); setSelectedId(last.id); setTool('select');
    } else if (last?.type==='line'||last?.type==='arrow'||last?.type==='doubleArrow') {
      saveHistory(objects, rasterImg?.src); setSelectedId(last.id); setTool('select');
    } else saveHistory(objects, rasterImg?.src);
  };

  // Polygon commit on double-click
  const commitPolygon = () => {
    if (polyPts.length < 3) { setPolyPts([]); return; }
    const pts = polyPts.flatMap(p => [p.x, p.y]);
    const appliedFill = fillMode === 'outline' ? '' : (fillColor || strokeColor);
    const appliedStroke = fillMode === 'fill' ? '' : strokeColor;
    const obj = { id:uid(), type:'polygon', points:pts, stroke:appliedStroke, strokeWidth, fill:appliedFill, draggable:true };
    const nx = [...objects, obj];
    setObjects(nx); saveHistory(nx, rasterImg?.src); setPolyPts([]); setTool('select');
  };

  // Text editing
  const startTextEdit = (id) => { skipBlurRef.current=true; setTimeout(()=>{skipBlurRef.current=false;},400); setEditingTextId(id); setSelectedId(id); };
  const commitText = () => {
    if (!editingTextId||skipBlurRef.current) return;
    const obj = objects.find(o=>o.id===editingTextId);
    if (obj&&(!obj.text||!obj.text.trim())) { const n=objects.filter(o=>o.id!==editingTextId); setObjects(n); saveHistory(n, rasterImg?.src); }
    else saveHistory(objects, rasterImg?.src);
    setEditingTextId(null);
  };
  useEffect(() => { if (editingTextId && textRef.current) { textRef.current.focus(); textRef.current.select(); } }, [editingTextId]);

  // Image import
  const onFileChange = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        const id=uid(); let w=img.width,h=img.height;
        if(w>CW*0.6){h=h*CW*0.6/w;w=CW*0.6;} if(h>CH*0.6){w=w*CH*0.6/h;h=CH*0.6;}
        setImages(prev=>({...prev,[id]:img}));
        const n=[...objects,{id,type:'image',x:50,y:50,width:w,height:h,draggable:true,src:reader.result}];
        setObjects(n); saveHistory(n, rasterImg?.src); setSelectedId(id); setTool('select');
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file); e.target.value='';
  };
  useEffect(() => { objects.forEach(o=>{if(o.type==='image'&&o.src&&!images[o.id]){const img=new window.Image();img.onload=()=>setImages(prev=>({...prev,[o.id]:img}));img.src=o.src;}}); }, [objects, images]);

  // Drag/Transform
  const onDragEnd = (id,e) => { const n=objects.map(o=>o.id===id?{...o,x:e.target.x(),y:e.target.y()}:o); setObjects(n); saveHistory(n, rasterImg?.src); };
  // For points-based shapes (line/arrow/doubleArrow/curve/polygon), translate the points array by the drag delta and reset the node's position to 0.
  // This keeps `obj.points` as the source of truth and avoids snap-back on re-render.
  const onDragEndPoints = (id, e) => {
    const dx = e.target.x(), dy = e.target.y();
    if (dx === 0 && dy === 0) return;
    const n = objects.map(o => {
      if (o.id !== id || !Array.isArray(o.points)) return o;
      const np = [];
      for (let i = 0; i < o.points.length; i += 2) { np.push(o.points[i] + dx, o.points[i+1] + dy); }
      return { ...o, points: np };
    });
    e.target.position({ x: 0, y: 0 });
    setObjects(n); saveHistory(n, rasterImg?.src);
  };
  const onTransformEnd = (id,e) => { const nd=e.target; const n=objects.map(o=>o.id===id?{...o,x:nd.x(),y:nd.y(),width:Math.max(5,nd.width()*nd.scaleX()),height:Math.max(5,nd.height()*nd.scaleY()),rotation:nd.rotation()}:o); nd.scaleX(1);nd.scaleY(1); setObjects(n); saveHistory(n, rasterImg?.src); };

  // Actions
  const deleteObj = () => { if(!selectedId) return; const n=objects.filter(o=>o.id!==selectedId); setObjects(n); saveHistory(n, rasterImg?.src); setSelectedId(null); };
  const duplicateObj = () => { const obj=objects.find(o=>o.id===selectedId); if(!obj) return; const id=uid(); const dup={...JSON.parse(JSON.stringify(obj)),id,x:(obj.x||0)+20,y:(obj.y||0)+20}; const n=[...objects,dup]; setObjects(n); saveHistory(n, rasterImg?.src); setSelectedId(id); if(obj.type==='image'&&images[obj.id]) setImages(prev=>({...prev,[id]:prev[obj.id]})); };
  const clearAll = () => {
    setObjects([]); setRasterImg(null); setSelectedId(null); setEditingTextId(null); setFloatSel(null); setSelection(null);
    const ctx = rasterRef.current.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, CW, CH);
    saveHistory([], null);
  };
  const handleSave = () => {
    setSaving(true); setSelectedId(null); setEditingTextId(null);
    if (floatSel) commitFloatingSelection();
    setTimeout(() => {
      try {
        const stage = stageRef.current;
        const prevSX = stage.scaleX(), prevSY = stage.scaleY();
        stage.scale({ x: 1, y: 1 });
        const dataUrl = stage.toDataURL({ pixelRatio: 2, width: CW, height: CH });
        stage.scale({ x: prevSX, y: prevSY });
        onSave(dataUrl);
      } catch (er) { console.error(er); }
      setSaving(false);
    }, 300);
  };

  // Render objects
  const renderObj = (obj) => {
    const click = () => { if(tool==='select') setSelectedId(obj.id); };
    const dp = obj.draggable ? { onDragEnd:e=>onDragEnd(obj.id,e), onClick:click, onTap:click } : {};
    // For points-based shapes (line/arrow/doubleArrow/curve/polygon), use points-translation handler
    const dpPts = obj.draggable ? { onDragEnd:e=>onDragEndPoints(obj.id,e), onClick:click, onTap:click } : {};

    if (obj.type==='freehand') {
      const dashArray = obj.tip === 'slash' ? [obj.strokeWidth*2, obj.strokeWidth] : undefined;
      return <Line key={obj.id} points={obj.points} stroke={obj.stroke} strokeWidth={obj.strokeWidth} opacity={obj.opacity} lineCap={obj.cap||'round'} lineJoin={obj.join||'round'} tension={0.5} dash={dashArray} listening={false} />;
    }
    if (obj.type==='line') return <Line key={obj.id} id={obj.id} points={obj.points} stroke={obj.stroke} strokeWidth={obj.strokeWidth} draggable hitStrokeWidth={20} {...dpPts} />;
    if (obj.type==='arrow') return <Arrow key={obj.id} id={obj.id} points={obj.points} stroke={obj.stroke} strokeWidth={obj.strokeWidth} fill={obj.stroke} pointerLength={12} pointerWidth={10} draggable hitStrokeWidth={20} {...dpPts} />;
    if (obj.type==='doubleArrow') return (<Group key={obj.id} id={obj.id} draggable {...dpPts}><Arrow points={obj.points} stroke={obj.stroke} strokeWidth={obj.strokeWidth} fill={obj.stroke} pointerLength={12} pointerWidth={10} /><Arrow points={[obj.points[2],obj.points[3],obj.points[0],obj.points[1]]} stroke={obj.stroke} strokeWidth={obj.strokeWidth} fill={obj.stroke} pointerLength={12} pointerWidth={10} /></Group>);
    if (obj.type==='curve') {
      const [x1,y1,cx,cy,x2,y2] = obj.points;
      const d = `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;
      return <Path key={obj.id} id={obj.id} data={d} stroke={obj.stroke} strokeWidth={obj.strokeWidth} draggable hitStrokeWidth={20} {...dpPts} />;
    }
    if (obj.type==='polygon') return <Line key={obj.id} id={obj.id} points={obj.points} stroke={obj.stroke} strokeWidth={obj.strokeWidth} fill={obj.fill||'rgba(0,0,0,0.001)'} closed draggable hitStrokeWidth={20} {...dpPts} onTransformEnd={e=>onTransformEnd(obj.id,e)} />;
    if (obj.type==='text') {
      const hidden = editingTextId===obj.id;
      const bw = obj.width||200, bh = obj.height||(obj.fontSize*2);
      return (<Group key={obj.id} id={obj.id} x={obj.x} y={obj.y} width={bw} height={bh} draggable onDragEnd={e=>onDragEnd(obj.id,e)} onClick={click} onTap={click} onDblClick={()=>startTextEdit(obj.id)} onDblTap={()=>startTextEdit(obj.id)} onTransformEnd={e=>onTransformEnd(obj.id,e)}>
        <Rect width={bw} height={bh} stroke={selectedId===obj.id?'#3b82f6':'#94a3b8'} strokeWidth={1} dash={[4,4]} fill="rgba(255,255,255,0.01)" opacity={selectedId===obj.id||editingTextId===obj.id?1:0.35} />
        {!hidden && <Text x={6} y={6} text={obj.text||(obj.isNew?'':'Double-click to type')} fontSize={obj.fontSize} fontFamily={obj.fontFamily} fontStyle={obj.fontStyle} textDecoration={obj.textDecoration||''} align={obj.align||'left'} fill={obj.fill} width={bw-12} height={bh-12} opacity={obj.text?1:0.35} listening={false} />}
      </Group>);
    }
    if (obj.type==='image'&&images[obj.id]) return <KImage key={obj.id} id={obj.id} x={obj.x} y={obj.y} image={images[obj.id]} width={obj.width} height={obj.height} draggable onDragEnd={e=>onDragEnd(obj.id,e)} onClick={click} onTap={click} onTransformEnd={e=>onTransformEnd(obj.id,e)} />;
    if (obj.type==='shape') {
      const st=obj.shapeType,w=Math.abs(obj.width||80),h=Math.abs(obj.height||60);
      const ox=(obj.width||0)<0?obj.x+(obj.width||0):obj.x, oy=(obj.height||0)<0?obj.y+(obj.height||0):obj.y;
      // Use invisible-but-hittable fill when no fill is set (outline mode) so clicks/drag work on interior
      const hitFill = obj.fill ? obj.fill : 'rgba(0,0,0,0.001)';
      const fp={stroke:obj.stroke||undefined,strokeWidth:obj.strokeWidth,fill:hitFill,draggable:true,...dp,onTransformEnd:e=>onTransformEnd(obj.id,e)};
      if(st==='rect') return <Rect key={obj.id} id={obj.id} x={ox} y={oy} width={w} height={h} {...fp} />;
      if(st==='roundRect') return <Rect key={obj.id} id={obj.id} x={ox} y={oy} width={w} height={h} cornerRadius={Math.min(14,w/4,h/4)} {...fp} />;
      if(st==='ellipse') return <Ellipse key={obj.id} id={obj.id} x={ox+w/2} y={oy+h/2} radiusX={w/2} radiusY={h/2} {...fp} />;
      if(st==='circle'){const rad=Math.min(w,h)/2; return <Circle key={obj.id} id={obj.id} x={ox+w/2} y={oy+h/2} radius={rad} {...fp} />;}
      if(st==='triangle') return <RegularPolygon key={obj.id} id={obj.id} x={ox+w/2} y={oy+h/2} sides={3} radius={Math.min(w,h)/2} {...fp} />;
      if(st==='diamond') return <RegularPolygon key={obj.id} id={obj.id} x={ox+w/2} y={oy+h/2} sides={4} radius={Math.min(w,h)/2} {...fp} />;
      if(st==='star') return <Star key={obj.id} id={obj.id} x={ox+w/2} y={oy+h/2} numPoints={5} innerRadius={Math.min(w,h)/4} outerRadius={Math.min(w,h)/2} {...fp} />;
      if(st==='hexagon') return <RegularPolygon key={obj.id} id={obj.id} x={ox+w/2} y={oy+h/2} sides={6} radius={Math.min(w,h)/2} {...fp} />;
      if(st==='pentagon') return <RegularPolygon key={obj.id} id={obj.id} x={ox+w/2} y={oy+h/2} sides={5} radius={Math.min(w,h)/2} {...fp} />;
      if(st==='octagon') return <RegularPolygon key={obj.id} id={obj.id} x={ox+w/2} y={oy+h/2} sides={8} radius={Math.min(w,h)/2} {...fp} />;
      if(st==='trapezoid') return <Line key={obj.id} id={obj.id} x={ox} y={oy} points={[w*0.15,0,w*0.85,0,w,h,0,h]} closed {...fp} />;
      if(st==='cylinder') return (<Group key={obj.id} id={obj.id} x={ox} y={oy} width={w} height={h} draggable {...dp} onTransformEnd={e=>onTransformEnd(obj.id,e)}>
        <Rect width={w} height={h} fill="rgba(0,0,0,0.001)" listening={true} />
        <Ellipse x={w/2} y={h*0.1} radiusX={w/2} radiusY={h*0.1} stroke={obj.stroke||undefined} strokeWidth={obj.strokeWidth} fill={obj.fill||undefined} />
        <Rect x={0} y={h*0.1} width={w} height={h*0.8} stroke="transparent" fill={obj.fill||undefined} />
        <Line points={[0,h*0.1,0,h*0.9]} stroke={obj.stroke||undefined} strokeWidth={obj.strokeWidth} />
        <Line points={[w,h*0.1,w,h*0.9]} stroke={obj.stroke||undefined} strokeWidth={obj.strokeWidth} />
        <Ellipse x={w/2} y={h*0.9} radiusX={w/2} radiusY={h*0.1} stroke={obj.stroke||undefined} strokeWidth={obj.strokeWidth} fill={obj.fill||undefined} />
      </Group>);
      if(st==='cloud'){const cx2=w/2,cy2=h/2,rx2=w/2,ry2=h/2;const pts=[];for(let i=0;i<12;i++){const a=i*Math.PI*2/12;const r2=i%2===0?1:0.75;pts.push(cx2+rx2*r2*Math.cos(a),cy2+ry2*r2*Math.sin(a));}return <Line key={obj.id} id={obj.id} x={ox} y={oy} points={pts} closed tension={0.4} {...fp} />;}
      return <Rect key={obj.id} id={obj.id} x={ox} y={oy} width={w} height={h} {...fp} />;
    }
    return null;
  };

  const editObj = editingTextId ? objects.find(o=>o.id===editingTextId) : null;
  const TB = ({id,active,onClick,children,title}) => (<button onClick={onClick} className={`h-8 px-2 rounded-md flex items-center justify-center gap-1 text-[11px] font-medium transition-all ${active?'bg-[#34A853] text-white shadow-sm':'text-gray-600 hover:bg-gray-100'}`} title={title} data-testid={`draw-tool-${id}`}>{children}</button>);

  // Curve preview line while picking points
  const curvePreview = curveStage.length >= 1 ? (() => {
    if (curveStage.length === 1) {
      return <Line points={[curveStage[0].x, curveStage[0].y, cursor.x, cursor.y]} stroke={strokeColor} strokeWidth={strokeWidth} dash={[4,4]} listening={false} />;
    }
    if (curveStage.length === 2) {
      const [p1, p2] = curveStage;
      const d = `M ${p1.x} ${p1.y} Q ${cursor.x} ${cursor.y} ${p2.x} ${p2.y}`;
      return <Path data={d} stroke={strokeColor} strokeWidth={strokeWidth} dash={[4,4]} listening={false} />;
    }
    return null;
  })() : null;

  // Polygon preview
  const polyPreview = polyPts.length >= 1 ? (() => {
    const pts = [...polyPts.flatMap(p => [p.x, p.y]), cursor.x, cursor.y];
    return <Line points={pts} stroke={strokeColor} strokeWidth={strokeWidth} dash={[4,4]} listening={false} />;
  })() : null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center" data-testid="drawing-canvas-modal" onClick={onClose}>
      <div className="bg-[#f0f0f0] rounded-xl shadow-2xl w-[97vw] max-w-[1400px] h-[94vh] flex flex-col overflow-hidden" onClick={e=>e.stopPropagation()}>
        {/* ── Titlebar ── */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white border-b border-gray-200 flex-shrink-0">
          <span className="font-bold text-[#34A853] text-[14px]">Desky Paint</span>
          {collabRoom ? (
            <CollabBadge roomId={collabRoom} peerCount={peerCount} status={collabStatus} className="ml-1" />
          ) : (
            <button
              onClick={startCollab}
              data-testid="paint-start-collab"
              className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 hover:bg-emerald-100">
              ● Go Live
            </button>
          )}

          {/* Image menu */}
          <div className="relative ml-2">
            <button onClick={()=>setShowImageMenu(!showImageMenu)} className="text-[11px] text-gray-600 hover:bg-gray-100 px-2 py-1 rounded" data-testid="draw-image-menu">Image ▾</button>
            {showImageMenu && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[180px] py-1">
                <button onClick={()=>applyImageOp('flipH')} className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-gray-100" data-testid="draw-img-flipH">Flip Horizontal</button>
                <button onClick={()=>applyImageOp('flipV')} className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-gray-100" data-testid="draw-img-flipV">Flip Vertical</button>
                <button onClick={()=>applyImageOp('rotate90')} className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-gray-100" data-testid="draw-img-rotate90">Rotate 90°</button>
                <div className="border-t border-gray-100 my-1" />
                <button onClick={()=>applyImageOp('invert')} className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-gray-100" data-testid="draw-img-invert">Invert Colors</button>
                <button onClick={()=>applyImageOp('clear')} className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-gray-100 text-red-500" data-testid="draw-img-clear">Clear Image</button>
              </div>
            )}
          </div>

          {/* Zoom */}
          <select value={zoom} onChange={e=>setZoom(parseFloat(e.target.value))} className="text-[10px] h-6 px-1 rounded border border-gray-200 bg-white ml-1" data-testid="draw-zoom">
            {ZOOMS.map(z => <option key={z} value={z}>{Math.round(z*100)}%</option>)}
          </select>

          <div className="flex-1" />
          <button onClick={handleSave} disabled={saving} className="font-bold text-white px-5 py-1.5 rounded-lg bg-[#34A853] hover:bg-[#2d9249] disabled:opacity-50 text-[12px]" data-testid="draw-save">{saving?'Saving...':'Save to Section'}</button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100 ml-1 text-[18px]" data-testid="draw-close">&times;</button>
        </div>

        {/* ── Main toolbar ── */}
        <div className="flex items-center gap-1 px-2 py-1.5 bg-white border-b border-gray-200 flex-wrap">
          {/* Selection tools */}
          <div className="flex gap-0.5 bg-gray-50 rounded-lg p-0.5 border border-gray-200">
            <TB id="select" active={tool==='select'} onClick={()=>setTool('select')} title="Select/Move"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"/></svg></TB>
            <TB id="rectSelect" active={tool==='rectSelect'} onClick={()=>setTool('rectSelect')} title="Rectangular Select (Ctrl+A all, Ctrl+C/X/V)"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2"><rect x="3" y="3" width="18" height="18" rx="1"/></svg></TB>
            <TB id="eyedropper" active={tool==='eyedropper'} onClick={()=>setTool('eyedropper')} title="Eyedropper — pick color"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 22l5-5"/><path d="M7 17l5 5"/><path d="M11 13l7-7"/><path d="M14 6l4 4"/><path d="M16 3l5 5"/></svg></TB>
            <TB id="magnifier" active={tool==='magnifier'} onClick={()=>setTool('magnifier')} title="Magnifier — click zoom in, right-click zoom out"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg></TB>
          </div>
          <div className="w-px h-7 bg-gray-200" />

          {/* Drawing tools */}
          <div className="flex gap-0.5 bg-gray-50 rounded-lg p-0.5 border border-gray-200">
            <TB id="pen" active={tool==='pen'} onClick={()=>setTool('pen')} title="Pencil"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.85 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg></TB>
            <TB id="brush" active={tool==='brush'} onClick={()=>setTool('brush')} title="Brush"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.06 11.9L16.96 4l3.48 3.48-7.9 7.9m-3.48-3.48l-3.48 3.48 3.48 3.48 3.48-3.48m-3.48-3.48l3.48 3.48"/></svg></TB>
            <TB id="airbrush" active={tool==='airbrush'} onClick={()=>setTool('airbrush')} title="Airbrush"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="17" cy="7" r="1"/><circle cx="19" cy="9" r="1"/><circle cx="21" cy="11" r="1"/><circle cx="14" cy="5" r="1"/><circle cx="16" cy="3" r="1"/><path d="M4 20L13 11"/><rect x="2" y="17" width="5" height="5" rx="1" transform="rotate(-45 4.5 19.5)"/></svg></TB>
            <TB id="marker" active={tool==='marker'} onClick={()=>setTool('marker')} title="Marker"><b className="text-[13px]">M</b></TB>
            <TB id="highlighter" active={tool==='highlighter'} onClick={()=>setTool('highlighter')} title="Highlighter"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" opacity=".4"><rect x="4" y="10" width="16" height="8" rx="2"/></svg></TB>
            <TB id="eraser" active={tool==='eraser'} onClick={()=>setTool('eraser')} title="Eraser"><span className="text-[14px]">⌫</span></TB>
          </div>
          <div className="w-px h-7 bg-gray-200" />

          {/* Line / curve / shape tools */}
          <div className="flex gap-0.5 bg-gray-50 rounded-lg p-0.5 border border-gray-200">
            <TB id="line" active={tool==='line'} onClick={()=>setTool('line')} title="Line"><svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" fill="none"><line x1="5" y1="19" x2="19" y2="5"/></svg></TB>
            <TB id="curve" active={tool==='curve'} onClick={()=>{setTool('curve'); setCurveStage([]);}} title="Curve — click 3 points"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 18 Q 9 3, 21 18"/></svg></TB>
            <TB id="arrow" active={tool==='arrow'} onClick={()=>setTool('arrow')} title="Arrow">→</TB>
            <TB id="doubleArrow" active={tool==='doubleArrow'} onClick={()=>setTool('doubleArrow')} title="Double Arrow">↔</TB>
            <TB id="polygon" active={tool==='polygon'} onClick={()=>{setTool('polygon'); setPolyPts([]);}} title="Polygon — click points, Enter/dbl-click to close"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12,2 22,8 18,21 6,21 2,8"/></svg></TB>
          </div>
          <div className="w-px h-7 bg-gray-200" />

          {/* Shapes dropdown */}
          <div className="relative">
            <button onClick={()=>setShowShapeMenu(!showShapeMenu)} className={`h-8 px-2.5 rounded-md flex items-center gap-1.5 text-[11px] font-medium border ${tool==='shape'?'bg-[#34A853] text-white border-[#34A853]':'text-gray-600 hover:bg-gray-100 border-gray-200 bg-white'}`} data-testid="draw-shapes-dropdown">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg> Shapes ▾
            </button>
            {showShapeMenu&&<div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-200 p-2.5 z-50 w-[260px]" data-testid="draw-shapes-menu">
              <p className="text-[9px] font-bold text-gray-400 uppercase px-1 mb-1.5">Basic</p>
              <div className="grid grid-cols-5 gap-1 mb-2">{SHAPES.filter(s=>s.cat==='basic').map(s=>(<button key={s.id} onClick={()=>{setShapeType(s.id);setTool('shape');setShowShapeMenu(false);}} className={`h-12 rounded-lg flex flex-col items-center justify-center text-[7px] font-medium ${shapeType===s.id&&tool==='shape'?'bg-[#34A853]/10 text-[#34A853] ring-1 ring-[#34A853]':'text-gray-500 hover:bg-gray-50 border border-gray-100'}`} data-testid={`draw-shape-${s.id}`}><span className="text-[14px]">{{'rect':'▬','roundRect':'▢','ellipse':'⬭','circle':'●','triangle':'▲','diamond':'◆','star':'★'}[s.id]||'□'}</span><span className="mt-0.5">{s.label}</span></button>))}</div>
              <p className="text-[9px] font-bold text-gray-400 uppercase px-1 mb-1.5">Flowchart</p>
              <div className="grid grid-cols-5 gap-1">{SHAPES.filter(s=>s.cat==='flow').map(s=>(<button key={s.id} onClick={()=>{setShapeType(s.id);setTool('shape');setShowShapeMenu(false);}} className={`h-12 rounded-lg flex flex-col items-center justify-center text-[7px] font-medium ${shapeType===s.id&&tool==='shape'?'bg-[#34A853]/10 text-[#34A853] ring-1 ring-[#34A853]':'text-gray-500 hover:bg-gray-50 border border-gray-100'}`} data-testid={`draw-shape-${s.id}`}><span className="text-[14px]">{{'hexagon':'⬡','pentagon':'⬠','octagon':'⯃','trapezoid':'⏢','cylinder':'⌗','cloud':'☁'}[s.id]||'□'}</span><span className="mt-0.5">{s.label}</span></button>))}</div>
            </div>}
          </div>
          <div className="w-px h-7 bg-gray-200" />

          <TB id="text" active={tool==='text'} onClick={()=>setTool('text')} title="Text — drag to create box"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="4,7 4,4 20,4 20,7"/><line x1="12" y1="4" x2="12" y2="20"/><line x1="8" y1="20" x2="16" y2="20"/></svg> Text</TB>
          <TB id="fill" active={tool==='fill'} onClick={()=>setTool('fill')} title="Paint Bucket"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2.5 18.5l9-9 5 5-9 9a1.5 1.5 0 01-2.12 0l-2.88-2.88a1.5 1.5 0 010-2.12z"/><path d="M7.5 13.5L16 5l3 3"/></svg> Fill</TB>
          <div className="w-px h-7 bg-gray-200" />

          <button onClick={()=>fileRef.current?.click()} className="h-8 px-2.5 rounded-md text-[11px] font-medium text-gray-600 hover:bg-gray-100 flex items-center gap-1 border border-gray-200 bg-white" data-testid="draw-import-image"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg> Image</button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} />

          {/* Text formatting */}
          {(tool==='text'||(selectedId&&objects.find(o=>o.id===selectedId)?.type==='text'))&&(
            <div className="flex items-center gap-1 ml-1 bg-blue-50 rounded-lg px-2 py-0.5 border border-blue-100">
              <select value={fontFamily} onChange={e=>{setFontFamily(e.target.value);if(selectedId){const n=objects.map(o=>o.id===selectedId&&o.type==='text'?{...o,fontFamily:e.target.value}:o);setObjects(n);}}} className="text-[10px] h-6 px-1 rounded border border-gray-200 bg-white" data-testid="draw-font-family">{FONTS.map(f=><option key={f} value={f}>{f}</option>)}</select>
              <select value={fontSize} onChange={e=>{setFontSize(+e.target.value);if(selectedId){const n=objects.map(o=>o.id===selectedId&&o.type==='text'?{...o,fontSize:+e.target.value}:o);setObjects(n);}}} className="text-[10px] h-6 w-12 px-1 rounded border border-gray-200 bg-white" data-testid="draw-font-size">{SIZES.map(s=><option key={s} value={s}>{s}</option>)}</select>
              <button onClick={()=>{const b=!fontBold;setFontBold(b);if(selectedId){const n=objects.map(o=>o.id===selectedId&&o.type==='text'?{...o,fontStyle:`${fontItalic?'italic ':''}${b?'bold':'normal'}`}:o);setObjects(n);}}} className={`w-6 h-6 rounded text-[12px] font-black ${fontBold?'bg-gray-800 text-white':'text-gray-400 hover:bg-gray-100'}`} data-testid="draw-bold">B</button>
              <button onClick={()=>{const i=!fontItalic;setFontItalic(i);if(selectedId){const n=objects.map(o=>o.id===selectedId&&o.type==='text'?{...o,fontStyle:`${i?'italic ':''}${fontBold?'bold':'normal'}`}:o);setObjects(n);}}} className={`w-6 h-6 rounded text-[12px] italic ${fontItalic?'bg-gray-800 text-white':'text-gray-400 hover:bg-gray-100'}`} data-testid="draw-italic">I</button>
              <button onClick={()=>{const u=!fontUnderline;setFontUnderline(u);if(selectedId){const n=objects.map(o=>o.id===selectedId&&o.type==='text'?{...o,textDecoration:u?'underline':''}:o);setObjects(n);}}} className={`w-6 h-6 rounded text-[12px] underline ${fontUnderline?'bg-gray-800 text-white':'text-gray-400 hover:bg-gray-100'}`} data-testid="draw-underline">U</button>
              <div className="w-px h-4 bg-gray-200" />
              {['left','center','right'].map(a=>(<button key={a} onClick={()=>{setTextAlign(a);if(selectedId){const n=objects.map(o=>o.id===selectedId&&o.type==='text'?{...o,align:a}:o);setObjects(n);}}} className={`w-6 h-6 rounded flex items-center justify-center ${textAlign===a?'bg-gray-800 text-white':'text-gray-400 hover:bg-gray-100'}`} data-testid={`draw-align-${a}`}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="3" y1="6" x2="21" y2="6"/><line x1={a==='right'?'9':'3'} y1="12" x2={a==='left'?'15':'21'} y2="12"/><line x1={a==='right'?'6':a==='center'?'4':'3'} y1="18" x2={a==='left'?'18':a==='center'?'20':'21'} y2="18"/></svg></button>))}
            </div>
          )}
          <div className="flex-1" />

          {/* Clipboard buttons */}
          <div className="flex gap-0.5 bg-gray-50 rounded-lg p-0.5 border border-gray-200">
            <button onClick={cutSelection} className="text-[10px] font-medium text-gray-600 px-2 py-1 rounded hover:bg-white" title="Cut (Ctrl+X)" data-testid="draw-cut">✂</button>
            <button onClick={copySelection} className="text-[10px] font-medium text-gray-600 px-2 py-1 rounded hover:bg-white" title="Copy (Ctrl+C)" data-testid="draw-copy">⧉</button>
            <button onClick={pasteClipboard} className="text-[10px] font-medium text-gray-600 px-2 py-1 rounded hover:bg-white" title="Paste (Ctrl+V)" data-testid="draw-paste">📋</button>
          </div>

          {selectedId&&<div className="flex gap-1 bg-red-50 rounded-lg px-1 py-0.5 border border-red-100 ml-1"><button onClick={duplicateObj} className="text-[10px] font-medium text-gray-600 px-2 py-1 rounded hover:bg-gray-100" data-testid="draw-duplicate">Dup</button><button onClick={deleteObj} className="text-[10px] font-medium text-red-500 px-2 py-1 rounded hover:bg-red-100" data-testid="draw-delete">Del</button></div>}
          <button onClick={undo} className="text-gray-500 p-1.5 rounded hover:bg-gray-100" data-testid="draw-undo" title="Undo (Ctrl+Z)"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1,4 1,10 7,10"/><path d="M3.51 15a9 9 0 105.64-11.36L1 10"/></svg></button>
          <button onClick={redo} className="text-gray-500 p-1.5 rounded hover:bg-gray-100" data-testid="draw-redo" title="Redo (Ctrl+Y)"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23,4 23,10 17,10"/><path d="M20.49 15a9 9 0 11-5.64-11.36L23 10"/></svg></button>
          <button onClick={clearAll} className="text-red-400 p-1.5 rounded hover:bg-red-50" data-testid="draw-clear" title="Clear canvas"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg></button>
        </div>

        {/* ── Properties strip (Colors + Line widths + Brush tips + Fill mode) ── */}
        <div className="flex items-center gap-2 px-3 py-1 bg-white border-b border-gray-200 flex-wrap">
          <span className="text-[8px] font-bold text-gray-400 uppercase">Color</span>
          {COLORS.map(c=><button key={c} onClick={()=>setStrokeColor(c)} className={`w-[18px] h-[18px] rounded-sm border ${strokeColor===c?'ring-2 ring-[#34A853] ring-offset-1 scale-125':'border-gray-200 hover:scale-110'} transition-all`} style={{background:c}} />)}
          <input type="color" value={strokeColor} onChange={e=>setStrokeColor(e.target.value)} className="w-[18px] h-[18px] rounded-sm cursor-pointer border-0 p-0" data-testid="draw-custom-color" />
          <div className="w-px h-5 bg-gray-200" />
          <span className="text-[8px] font-bold text-gray-400 uppercase">Fill</span>
          <button onClick={()=>setFillColor('')} className={`w-[18px] h-[18px] rounded-sm border text-[8px] flex items-center justify-center ${!fillColor?'ring-2 ring-[#34A853] ring-offset-1':'border-gray-200'}`} style={{background:'#fff'}}>∅</button>
          {COLORS.slice(0,10).map(c=><button key={`f${c}`} onClick={()=>setFillColor(c)} className={`w-[18px] h-[18px] rounded-sm border ${fillColor===c?'ring-2 ring-[#34A853] ring-offset-1 scale-125':'border-gray-200 hover:scale-110'} transition-all`} style={{background:c,opacity:0.7}} />)}
          <input type="color" value={fillColor||'#ffffff'} onChange={e=>setFillColor(e.target.value)} className="w-[18px] h-[18px] rounded-sm cursor-pointer border-0 p-0" />

          <div className="w-px h-5 bg-gray-200" />
          {/* Line width presets (jspaint-style box) */}
          <span className="text-[8px] font-bold text-gray-400 uppercase">Line</span>
          <div className="flex gap-0.5 bg-gray-50 rounded border border-gray-200 p-0.5">
            {LINE_WIDTHS.map(w => (
              <button key={w} onClick={()=>setStrokeWidth(w)} className={`w-6 h-5 flex items-center justify-center rounded ${strokeWidth===w?'bg-[#34A853]/20 ring-1 ring-[#34A853]':'hover:bg-white'}`} data-testid={`draw-linewidth-${w}`}>
                <div style={{width: '16px', height: `${Math.min(w, 8)}px`, background: '#1e293b', borderRadius: '1px'}} />
              </button>
            ))}
          </div>

          {/* Brush tips (only when brush tool is active) */}
          {tool === 'brush' && (
            <>
              <div className="w-px h-5 bg-gray-200" />
              <span className="text-[8px] font-bold text-gray-400 uppercase">Tip</span>
              <div className="flex gap-0.5 bg-gray-50 rounded border border-gray-200 p-0.5">
                {BRUSH_TIPS.map(b => (
                  <button key={b.id} onClick={()=>setBrushTip(b.id)} className={`w-6 h-5 text-[14px] rounded ${brushTip===b.id?'bg-[#34A853]/20 ring-1 ring-[#34A853]':'hover:bg-white'}`} data-testid={`draw-brushtip-${b.id}`}>{b.label}</button>
                ))}
              </div>
            </>
          )}

          {/* Fill mode (for shapes) */}
          {(tool === 'shape' || tool === 'polygon') && (
            <>
              <div className="w-px h-5 bg-gray-200" />
              <span className="text-[8px] font-bold text-gray-400 uppercase">Mode</span>
              <div className="flex gap-0.5 bg-gray-50 rounded border border-gray-200 p-0.5">
                {FILL_MODES.map(f => (
                  <button key={f.id} onClick={()=>setFillMode(f.id)} className={`text-[9px] px-1.5 h-5 rounded ${fillMode===f.id?'bg-[#34A853] text-white':'text-gray-500 hover:bg-white'}`} title={f.desc} data-testid={`draw-fillmode-${f.id}`}>{f.label}</button>
                ))}
              </div>
            </>
          )}

          <div className="w-px h-5 bg-gray-200" />
          <button onClick={()=>setShowGrid(g=>!g)} className={`text-[9px] font-bold px-2 py-1 rounded ${showGrid?'bg-blue-100 text-blue-600':'text-gray-400 hover:bg-gray-100'}`} data-testid="draw-grid-toggle">Grid</button>
        </div>

        {/* ── Canvas ── */}
        <div className="flex-1 overflow-auto bg-[#d4d4d4] flex items-center justify-center p-3 relative" style={{cursor: tool==='select'?'default' : tool==='text'?'text' : tool==='eyedropper'?'crosshair' : tool==='magnifier'?(zoom<8?'zoom-in':'zoom-out') : 'crosshair'}}>
          <div className="relative shadow-xl rounded-md overflow-visible" style={{width: CW*zoom, minWidth: CW*zoom, height: CH*zoom}}>
            {showGrid&&<div className="absolute inset-0 pointer-events-none z-10 rounded-md" style={{backgroundImage:'linear-gradient(rgba(0,0,0,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(0,0,0,0.06) 1px,transparent 1px)',backgroundSize:`${24*zoom}px ${24*zoom}px`}} />}
            <Stage
              ref={stageRef}
              width={CW*zoom}
              height={CH*zoom}
              scaleX={zoom}
              scaleY={zoom}
              onMouseDown={onStageDown}
              onMouseMove={onStageMove}
              onMouseUp={onStageUp}
              onTouchStart={onStageDown}
              onTouchMove={onStageMove}
              onTouchEnd={onStageUp}
              onDblClick={() => { if (tool==='polygon') commitPolygon(); }}
              onContextMenu={e => { e.evt.preventDefault(); if (tool==='magnifier') { const idx = ZOOMS.indexOf(zoom); setZoom(ZOOMS[Math.max(0, idx-1)]); } }}
              data-testid="drawing-canvas"
            >
              <Layer>
                {rasterImg ? <KImage image={rasterImg} x={0} y={0} width={CW} height={CH} name="raster" listening={true} /> : <Rect x={0} y={0} width={CW} height={CH} fill="#ffffff" name="bg" />}
                {objects.map(renderObj)}

                {/* Floating selection preview */}
                {floatSel && (
                  <Group
                    x={floatSel.x} y={floatSel.y}
                    onMouseDown={e => { const pos = getPos(); setFloatSel(f => ({...f, dragging: true, dragOffX: pos.x - f.x, dragOffY: pos.y - f.y})); e.cancelBubble = true; }}
                  >
                    <KImage image={floatSel.image} x={0} y={0} width={floatSel.w} height={floatSel.h} />
                    <Rect x={0} y={0} width={floatSel.w} height={floatSel.h} stroke="#000" strokeWidth={1} dash={[4,4]} dashOffset={floatSel.dashOffset||0} listening={false} />
                    <Rect x={0} y={0} width={floatSel.w} height={floatSel.h} stroke="#fff" strokeWidth={1} dash={[4,4]} dashOffset={(floatSel.dashOffset||0)+4} listening={false} />
                  </Group>
                )}

                {/* Rectangular selection marquee */}
                {selection && (
                  <>
                    <Rect x={selection.x} y={selection.y} width={selection.w} height={selection.h} stroke="#000" strokeWidth={1} dash={[4,4]} dashOffset={selection.dashOffset||0} fill="transparent" listening={false} />
                    <Rect x={selection.x} y={selection.y} width={selection.w} height={selection.h} stroke="#fff" strokeWidth={1} dash={[4,4]} dashOffset={(selection.dashOffset||0)+4} fill="transparent" listening={false} />
                  </>
                )}

                {curvePreview}
                {polyPreview}
                {polyPts.map((p, i) => <Circle key={`pp${i}`} x={p.x} y={p.y} radius={4/zoom} fill="#34A853" listening={false} />)}
                {curveStage.map((p, i) => <Circle key={`cp${i}`} x={p.x} y={p.y} radius={4/zoom} fill="#3b82f6" listening={false} />)}

                <Transformer ref={trRef} boundBoxFunc={(o,n)=>({...n,width:Math.max(5,n.width),height:Math.max(5,n.height)})} />
              </Layer>
            </Stage>
            {editingTextId&&editObj&&(()=>{const stageEl=stageRef.current?.container();if(!stageEl)return null;const sb=stageEl.getBoundingClientRect();const sx=sb.width/CW,sy=sb.height/CH;const tw=Math.max(80,(editObj.width||200)*sx),th=Math.max(30,(editObj.height||editObj.fontSize*2)*sy);return <textarea ref={textRef} value={editObj.text||''} data-testid="draw-text-input" onChange={e=>{const v=e.target.value;setObjects(prev=>prev.map(o=>o.id===editingTextId?{...o,text:v}:o));}} onKeyDown={e=>{if(e.key==='Escape')commitText();}} onBlur={commitText} placeholder="Type here..." className="absolute z-20 border-2 border-blue-400 bg-white/95 outline-none resize overflow-auto p-1.5 shadow-lg" style={{left:editObj.x*sx,top:editObj.y*sy,width:tw,height:th,minWidth:80,minHeight:30,fontSize:Math.max(10,editObj.fontSize*sy),fontFamily:editObj.fontFamily,fontWeight:editObj.fontStyle?.includes('bold')?'bold':'normal',fontStyle:editObj.fontStyle?.includes('italic')?'italic':'normal',textDecoration:editObj.textDecoration||'none',textAlign:editObj.align||'left',color:editObj.fill||'#1e293b',lineHeight:1.3}} autoFocus />;})()}
          </div>
        </div>

        {/* ── Status bar (jspaint-style) ── */}
        <div className="flex items-center gap-3 px-3 py-1 bg-white border-t border-gray-200 text-[9px] text-gray-400">
          <span data-testid="draw-status-dims">{CW}×{CH}px</span>
          <span data-testid="draw-status-zoom">{Math.round(zoom*100)}%</span>
          <span data-testid="draw-status-cursor">Cursor: {cursor.x}, {cursor.y}</span>
          {selection && <span>Selection: {Math.round(selection.w)}×{Math.round(selection.h)}</span>}
          {floatSel && <span className="text-blue-500">Floating · {Math.round(floatSel.w)}×{Math.round(floatSel.h)} @ {Math.round(floatSel.x)},{Math.round(floatSel.y)}</span>}
          {tool === 'curve' && <span className="text-blue-500">Curve: click {3 - curveStage.length} more point(s)</span>}
          {tool === 'polygon' && polyPts.length > 0 && <span className="text-blue-500">Polygon: {polyPts.length} pts · double-click or Enter to close</span>}
          <span>{objects.length} obj</span>
          <div className="flex-1" />
          <span>Tool: <b className="text-gray-600">{tool}</b>{tool==='shape'?` (${shapeType})`:''}</span>
          <span className="text-gray-300">Ctrl+A · Ctrl+C/X/V · Esc cancel</span>
        </div>
      </div>
    </div>
  );
}
